import React, { useEffect, useState } from "react";
import "./TravelerWaitlist.css";
import "./FlightDetails.css"; /* for .fd-btn family */
import RequestTimeline, { timelineStepFor } from "./RequestTimeline";
import { statusStamp } from "../utils/status";
import StepIndicator from "./StepIndicator";
import { onSnapshot, collection, doc, updateDoc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { pickWaitlistDoc } from "../utils/liveRequest";
import { showToast } from "./Toast";
import { WaitlistSkeleton } from "./Skeleton";
import ConfirmModal from "./ConfirmModal";
import { useNavigate, useLocation } from "react-router-dom";

/* ── Inline SVG icon set (matches FlightDetails) ── */
const Icon = {
  plane: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21 4 19 2c-2-2-4-2-5.5-.5L10 5 1.8 6.2c-.8.2-1.3.8-1.1 1.7l2.7 4.5c.3.5 1 .6 1.5.2L7.5 10l3.5 8.5c.2.5.8.8 1.3.6l4.5-2c.7-.2.8-.9 1-1.9z"/>
    </svg>
  ),
  planeFilled: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M2.5 19h19v2h-19zM22.07 9.64c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 2.59 4.49.74 1.28 1.56-.42 5.18-1.39 4.32-1.16 5.58-1.5c.81-.22 1.28-1.05 1.06-1.84z"/>
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 1 1 8 0v4"/>
    </svg>
  ),
  package: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  inbox: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  copy: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
};

export default function TravelerWaitlist() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("SEARCHING");
  const [loading, setLoading] = useState(true);
  const [opsReviewed, setOpsReviewed] = useState(false);
  const [firstMileOTP, setFirstMileOTP] = useState(null);
  const [lastMileOTP, setLastMileOTP] = useState(null);
  const [confirmedForSender, setConfirmedForSender] = useState(null);
  const [travelerDocRef, setTravelerDocRef] = useState(null);
  const [senderDetails, setSenderDetails] = useState(null);
  const [reportSent, setReportSent] = useState(false);
  const [itemReport, setItemReport] = useState(null); // itemNotReceivedReport from the doc (live)
  const [reportLoading, setReportLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [summary, setSummary] = useState(null);
  const [earningAmount, setEarningAmount] = useState(null);
  const [earningStatus, setEarningStatus] = useState("PENDING");
  const [overallEarning, setOverallEarning] = useState(0);
  const [activeTab, setActiveTab] = useState("STATUS");
  const [faqOpen, setFaqOpen] = useState(null);
  const [faqQuestion, setFaqQuestion] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Last Mile OTP entry
  const [lmDigits, setLmDigits] = useState(["", "", "", "", "", ""]);
  const [lmError, setLmError] = useState("");
  const [lmLoading, setLmLoading] = useState(false);
  const [lmSuccess, setLmSuccess] = useState(false);
  const lmRefs = React.useRef([]);

  // Confirm modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // "Item ready for pickup" slider
  const [readySliderX, setReadySliderX] = useState(0);
  const [readySliderDone, setReadySliderDone] = useState(false);
  const [showPickupCard, setShowPickupCard] = useState(false);
  const [pickupTime, setPickupTime] = useState("");
  const [pickupSaving, setPickupSaving] = useState(false);
  const [pickupConfirmed, setPickupConfirmed] = useState(false);
  const readyTrackRef = React.useRef(null);
  const isReadyDragging = React.useRef(false);
  const readyStartX = React.useRef(0);

  // Slider state
  const [sliderX, setSliderX] = useState(0);
  const [sliderDone, setSliderDone] = useState(false);
  const sliderTrackRef = React.useRef(null);
  const isDragging = React.useRef(false);
  const startX = React.useRef(0);
  const THUMB = 56;
  const THRESHOLD = 0.78;

  const otpComplete = lmDigits.join("").length === 6;

  const getMax = () => {
    if (!sliderTrackRef.current) return 200;
    return sliderTrackRef.current.offsetWidth - THUMB;
  };
  const onDragStart = (clientX) => {
    if (sliderDone || !otpComplete) return;
    isDragging.current = true;
    startX.current = clientX - sliderX;
  };
  const onDragMove = (clientX) => {
    if (!isDragging.current) return;
    const next = Math.min(Math.max(0, clientX - startX.current), getMax());
    setSliderX(next);
  };
  const onDragEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (sliderX / getMax() >= THRESHOLD) {
      setSliderX(getMax());
      setShowConfirmModal(true);
    } else {
      setSliderX(0);
    }
  };
  const onSliderKeyDown = (e) => {
    if (sliderDone || !otpComplete) return;
    const max = getMax();
    const step = Math.max(20, max * 0.1);
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setSliderX((x) => Math.min(max, x + step));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setSliderX((x) => Math.max(0, x - step));
    } else if (e.key === "Home") {
      e.preventDefault();
      setSliderX(0);
    } else if (e.key === "End" || e.key === " " || e.key === "Enter") {
      e.preventDefault();
      setSliderX(max);
      setShowConfirmModal(true);
    }
  };

  // ---- "Item ready" slider drag handlers ----
  const getReadyMax = () => (readyTrackRef.current ? readyTrackRef.current.offsetWidth - THUMB : 200);
  const onReadyDragStart = (clientX) => {
    if (readySliderDone) return;
    isReadyDragging.current = true;
    readyStartX.current = clientX - readySliderX;
  };
  const onReadyDragMove = (clientX) => {
    if (!isReadyDragging.current) return;
    setReadySliderX(Math.min(Math.max(0, clientX - readyStartX.current), getReadyMax()));
  };
  const onReadyDragEnd = () => {
    if (!isReadyDragging.current) return;
    isReadyDragging.current = false;
    if (readySliderX / getReadyMax() >= THRESHOLD) {
      setReadySliderX(getReadyMax());
      setShowPickupCard(true);
    } else {
      setReadySliderX(0);
    }
  };
  const reportItemNotReceived = async () => {
    setReportLoading(true);
    try {
      await updateDoc(travelerDocRef, {
        itemNotReceivedReport: { reportedAt: new Date().toISOString(), resolved: false },
      });
      // Ping admin alert
      await setDoc(doc(db, "meta", "adminAlerts"), {
        itemNotReceivedAlert: {
          phone: location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER"),
          name: [summary?.firstName, summary?.lastName].filter(Boolean).join(" "),
          reportedAt: new Date().toISOString(),
          requestId: summary?.requestId || "",
          route: `${summary?.fromCity || "?"} → ${summary?.toCity || "?"}`,
        },
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setReportSent(true);
      showToast("Report submitted. Team will contact you shortly.", "success");
    } catch {
      showToast("Failed to submit report. Try again.", "error");
    } finally {
      setReportLoading(false);
    }
  };

  const submitPickupReady = async () => {
    setPickupSaving(true);
    try {
      const phone = location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER");
      const readyAt = new Date().toISOString();
      await updateDoc(travelerDocRef, {
        itemReadyForPickup: true,
        itemReadyAt: readyAt,
        ...(pickupTime ? { pickupAvailableFrom: pickupTime } : {}),
      });
      // Ping admin alert doc — picked up by existing adminAlerts listener in dashboard
      await setDoc(doc(db, "meta", "adminAlerts"), {
        itemReadyAlert: {
          phone,
          readyAt,
          ...(pickupTime ? { availableFrom: pickupTime } : {}),
          requestId: summary?.requestId || "",
          route: `${summary?.fromCity || "?"} → ${summary?.toCity || "?"}`,
        },
        updatedAt: readyAt,
      }, { merge: true });
      setReadySliderDone(true);
      setPickupConfirmed(true);
      setShowPickupCard(false);
      showToast("Marked as ready for pickup!", "success");
    } catch {
      showToast("Failed to update. Try again.", "error");
      setReadySliderX(0);
    } finally {
      setPickupSaving(false);
    }
  };

  // Pull-to-refresh
  const [refreshing, setRefreshing] = useState(false);
  const pullStartY = React.useRef(0);
  const isPulling = React.useRef(false);
  const handlePullStart = (e) => {
    if (window.scrollY === 0) {
      pullStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  };
  const handlePullEnd = (e) => {
    if (!isPulling.current) return;
    isPulling.current = false;
    const pullDistance = (e.changedTouches?.[0]?.clientY || 0) - pullStartY.current;
    if (pullDistance > 80) {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), 1500);
    }
  };

  const handleCopy = () => {
    if (!firstMileOTP) return;
    navigator.clipboard.writeText(String(firstMileOTP));
    setCopied(true);
    if (typeof navigator.vibrate === "function") navigator.vibrate(15);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLmChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...lmDigits];
    next[idx] = val;
    setLmDigits(next);
    setLmError("");
    if (val && idx < 5) lmRefs.current[idx + 1]?.focus();
  };
  const handleLmKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !lmDigits[idx] && idx > 0) lmRefs.current[idx - 1]?.focus();
  };
  const handleLmPaste = (e) => {
    const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (p.length === 6) {
      setLmDigits(p.split(""));
      lmRefs.current[5]?.focus();
    }
  };

  const confirmDelivery = async () => {
    const entered = lmDigits.join("");
    if (entered.length < 6) { setLmError("Please enter all 6 digits"); return false; }
    if (String(lastMileOTP) !== entered) { setLmError("Incorrect OTP. Please check with the sender."); return false; }
    setLmLoading(true);
    try {
      const ts = new Date().toISOString();
      await updateDoc(travelerDocRef, statusStamp("COMPLETED", ts));
      if (confirmedForSender?.senderPhone && confirmedForSender?.senderRequestKey) {
        const sRef = doc(db, "users", confirmedForSender.senderPhone, "SenderRequests", confirmedForSender.senderRequestKey);
        await updateDoc(sRef, statusStamp("COMPLETED", ts));
      }
      setLmSuccess(true);
      setSliderDone(true);
      setStatus("COMPLETED");
      showToast("Delivery confirmed successfully!", "success");
      return true;
    } catch {
      setLmError("Something went wrong. Try again.");
      setSliderX(0);
      showToast("Failed to confirm delivery. Please try again.", "error");
      return false;
    } finally {
      setLmLoading(false);
      setShowConfirmModal(false);
    }
  };

  useEffect(() => {
    const phone = location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER");
    const role = location.state?.role || localStorage.getItem("USER_ROLE");
    if (!phone || (role && role !== "TRAVELER")) {
      navigate("/login", { replace: true });
      return;
    }

    let unsub = null;
    const startListening = async () => {
      // The request to display = live one if any, else the user's latest
      // (latestRequestKey). Without this, a random sibling (e.g. an old
      // CANCELLED request) could win by document-ID order.
      let latestRequestKey = null;
      try {
        const userSnap = await getDoc(doc(db, "users", phone));
        latestRequestKey = userSnap.exists() ? userSnap.data().latestRequestKey || null : null;
      } catch { /* fall back to createdAt ordering inside the picker */ }

      unsub = onSnapshot(collection(db, "users", phone, "TravelerRequests"), (snap) => {
        const activeDoc = pickWaitlistDoc(snap.docs, latestRequestKey);
        if (!activeDoc) {
          setStatus("SEARCHING");
          setLoading(false);
          return;
        }
        const data = activeDoc.data();
        setStatus(data.status || (data.requestStatus === "MATCH_FOUND" ? "MATCHED" : data.requestStatus) || "NEW_ORDER");
        setOpsReviewed(!!data.opsReviewed);
        setFirstMileOTP(data.FirstMileOTP || null);
        setLastMileOTP(data.LastMileOTP || null);
        const cfs = data.confirmedForSender || null;
        setConfirmedForSender(cfs);
        // Fetch sender request details for the Sender tab
        if (cfs?.senderPhone && cfs?.senderRequestKey) {
          getDoc(doc(db, "users", cfs.senderPhone, "SenderRequests", cfs.senderRequestKey))
            .then((s) => { if (s.exists()) setSenderDetails({ phone: cfs.senderPhone, ...s.data() }); })
            .catch(() => {});
        }
        setEarningAmount(data.earningAmount != null ? data.earningAmount : null);
        setEarningStatus(data.earningStatus || "PENDING");
        // Live report state from the doc — shows "submitted" across refreshes
        // and flips to "resolved" the moment ops resolves it in the admin panel.
        setItemReport(data.itemNotReceivedReport || null);
        if (data.itemNotReceivedReport?.resolved) setReportSent(false); // allow re-reporting after resolution
        // Persist "item ready for pickup" from the doc so a refresh keeps the
        // confirmed state instead of re-showing the slider (which would let the
        // traveller re-mark it and re-ping the admin).
        if (data.itemReadyForPickup) {
          setPickupConfirmed(true);
          if (data.pickupAvailableFrom) setPickupTime(data.pickupAvailableFrom);
        }
        const total = snap.docs.reduce((sum, d) => {
          const dd = d.data();
          if (dd.earningStatus !== "PAID") return sum;
          const amt = parseFloat(dd.earningAmount);
          return sum + (Number.isFinite(amt) ? amt : 0);
        }, 0);
        setOverallEarning(Math.round(total * 100) / 100);
        setTravelerDocRef(doc(db, "users", phone, "TravelerRequests", activeDoc.id));
        setSummary({
          requestId: data.uniqueKey,
          fromCity: data.from?.city,
          toCity: data.to?.city,
          airline: data.flightDetails?.airline,
          travelDate: data.flightDetails?.travelDate,
          departureTime: data.flightDetails?.departureTime,
          baggageSpace: data.flightDetails?.baggageSpace,
          spaceAvailable: data.flightDetails?.spaceAvailableWhen,
          carryType: data.flightDetails?.carryType,
          firstName: data.flightDetails?.firstName,
          lastName: data.flightDetails?.lastName,
          statusTimestamps: data.statusTimestamps || {},
          createdAt: data.createdAt || null,
          // Set server-side in saveUserData when this is the traveller's
          // FIRST-EVER trip AND the route has an active campaign. Owed even if
          // no match is ever found, so it must be visible here — the campaign
          // landing page promised it before they signed up.
          campaignEligible: data.campaignEligible === true,
          campaignReward: data.campaignReward ?? null,
        });
        setLoading(false);
      });
    };

    startListening();
    return () => { if (unsub) unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const getStep = () => {
    switch (status) {
      case "COMPLETED": return 8;
      case "IN_PROGRESS": return 7;
      case "MATCHED": return 6;
      case "SEARCHING": return 4;
      default: return 3;
    }
  };

  const getStepLabel = () => {
    switch (status) {
      case "COMPLETED":   return "Delivery complete";
      case "IN_PROGRESS": return "Item in transit";
      case "MATCHED":     return "Match confirmed";
      case "SEARCHING":   return "Looking for match";
      default:            return "Request received";
    }
  };

  if (loading) {
    return (
      <div className="waitlist-page">
        <div className="waitlist-card">
          <WaitlistSkeleton />
        </div>
      </div>
    );
  }

  const sliderPct = Math.round(sliderX / Math.max(1, getMax()) * 100);

  const keepLooking = async () => {
    if (!travelerDocRef) return;
    setActionLoading(true);
    try {
      await updateDoc(travelerDocRef, statusStamp("SEARCHING"));
      setStatus("SEARCHING");
      showToast("We'll keep looking for a matching sender.", "success");
    } catch {
      showToast("Something went wrong. Try again.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const cancelAndNew = async () => {
    if (travelerDocRef) {
      setActionLoading(true);
      try {
        const ts = new Date().toISOString();
        await updateDoc(travelerDocRef, { ...statusStamp("CANCELLED", ts), cancelledAt: ts });
      } catch { /* navigate anyway */ }
      setActionLoading(false);
    }
    navigate("/selection", { state: { phoneNumber: location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER") } });
  };

  if (status === "NO_MATCH") {
    return (
      <div className="waitlist-page">
        <div className="waitlist-card" style={{ textAlign: "center", padding: "40px 24px" }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: "2rem" }}>
            🔍
          </div>
          <h2 style={{ fontSize: "1.3rem", fontWeight: 800, color: "#1a1a2e", marginBottom: 8 }}>No match found yet</h2>
          <p style={{ color: "#667781", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: 8, maxWidth: 340, margin: "0 auto 8px" }}>
            We couldn't find a sender matching your route
            {summary?.fromCity && summary?.toCity ? <> (<strong>{summary.fromCity} → {summary.toCity}</strong>)</> : ""} and dates yet.
          </p>
          <p style={{ color: "#667781", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: 28, maxWidth: 340, margin: "0 auto 28px" }}>
            Want us to keep looking, or cancel and register again?
          </p>
          <button
            type="button"
            className="fd-btn fd-btn--primary fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto 12px" }}
            onClick={keepLooking}
            disabled={actionLoading}
          >
            {actionLoading ? "Please wait…" : "Keep looking"}
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto 16px", background: "#fff", color: "#dc2626", border: "1.5px solid #fca5a5" }}
            onClick={cancelAndNew}
            disabled={actionLoading}
          >
            Cancel &amp; register again
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--ghost fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            onClick={() => window.dispatchEvent(new CustomEvent("tx-chat-open", { detail: { prefill: "Hi TurantX, no match was found for my traveller registration. Can you help?" } }))}
          >
            💬 Chat with support
          </button>
        </div>
      </div>
    );
  }

  if (status === "CANCELLED") {
    return (
      <div className="waitlist-page">
        <div className="waitlist-card" style={{ textAlign: "center", padding: "40px 24px" }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: "#fff2f2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: "2rem" }}>
            ❌
          </div>
          <h2 style={{ fontSize: "1.3rem", fontWeight: 800, color: "#1a1a2e", marginBottom: 8 }}>Registration Cancelled</h2>
          <p style={{ color: "#667781", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: 28, maxWidth: 320, margin: "0 auto 28px" }}>
            Your traveller registration has been cancelled. If this was a mistake, please reach out to us on TurantChat.
          </p>
          <button
            type="button"
            className="fd-btn fd-btn--primary fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto 16px" }}
            onClick={() => navigate("/selection", { state: { phoneNumber: location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER") } })}
          >
            <span style={{ marginRight: 6 }}>+</span> Register again
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--ghost fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            onClick={() => window.dispatchEvent(new CustomEvent("tx-chat-open", { detail: { prefill: "Hi TurantX, my traveller registration was cancelled. Can you help?" } }))}
          >
            💬 Chat with support
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="waitlist-page" onTouchStart={handlePullStart} onTouchEnd={handlePullEnd}>
      {refreshing && (
        <div className="pull-refresh-indicator">
          <div className="refresh-spinner" />
          <span>Refreshing…</span>
        </div>
      )}

      <div className="waitlist-card">
        <StepIndicator current={getStep()} total={8} label={getStepLabel()} />

        <h2 className="wl-title">
          <span className="wl-title-icon" aria-hidden>{Icon.plane}</span>
          You're signed up as a Traveller
        </h2>
        <p className="wl-subtitle">
          When a sender has a request along your route, we'll notify you via <strong>SMS or TurantChat</strong>.
        </p>

        <button
          type="button"
          className="wl-dashboard-btn"
          onClick={() => navigate("/dashboard", { state: { phoneNumber: location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER") } })}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="wl-dashboard-btn-icon">
            <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
          </svg>
          My Dashboard
        </button>

        {/* ---------- TABS ---------- */}
        <div className="tabs" role="tablist" aria-label="Waitlist sections">
          <button
            role="tab"
            aria-selected={activeTab === "STATUS"}
            className={activeTab === "STATUS" ? "tab active" : "tab"}
            onClick={() => setActiveTab("STATUS")}
          >Status</button>
          <button
            role="tab"
            aria-selected={activeTab === "REQUEST"}
            className={activeTab === "REQUEST" ? "tab active" : "tab"}
            onClick={() => setActiveTab("REQUEST")}
          >Your Request</button>
          {(status === "MATCHED" || status === "IN_PROGRESS") && (
            <button
              role="tab"
              aria-selected={activeTab === "SENDER"}
              className={activeTab === "SENDER" ? "tab active" : "tab"}
              onClick={() => setActiveTab("SENDER")}
            >Your Sender</button>
          )}
          <button
            role="tab"
            aria-selected={activeTab === "EARNING"}
            className={activeTab === "EARNING" ? "tab active" : "tab"}
            onClick={() => setActiveTab("EARNING")}
          >Your Earning</button>
          <button
            role="tab"
            aria-selected={activeTab === "FAQ"}
            className={activeTab === "FAQ" ? "tab active" : "tab"}
            onClick={() => setActiveTab("FAQ")}
          >FAQ</button>
        </div>

        {/* ---------- STATUS TAB ---------- */}
        {activeTab === "STATUS" && (
          <div className="wl-panel" role="tabpanel">

            {opsReviewed && (
              <div className="ops-badge">
                <span className="dot-green" aria-hidden></span>
                Reviewed by TurantX Operations
              </div>
            )}

            {(status === "NEW_ORDER" || status === "SEARCHING") && (
              <div className="status-banner">
                <span className="status-banner-icon" aria-hidden>{Icon.shield}</span>
                <div className="status-banner-text">
                  <strong>You're all set.</strong> We'll notify you via SMS or TurantChat when a sender request comes up on your route. There's <strong>no obligation</strong> to accept.
                </div>
              </div>
            )}

            {status === "MATCHED" && (
              <div className="status-banner">
                <span className="status-banner-icon" aria-hidden>{Icon.check}</span>
                <div className="status-banner-text">
                  <strong>Good news — a match has come up on your route.</strong> Our team will share the details via SMS or TurantChat shortly.
                </div>
              </div>
            )}

            {/* ---- STEP 1 OTP ---- */}
            {status !== "IN_PROGRESS" && status !== "COMPLETED" && (
              status !== "MATCHED" ? (
                <div className="otp-card is-locked" aria-live="polite">
                  <div className="otp-card-badge is-muted">Step 1 · Pickup OTP</div>
                  <div className="otp-card-icon-wrap" aria-hidden>{Icon.lock}</div>
                  <h3 className="otp-card-title">Pickup OTP</h3>
                  <p className="otp-card-subtitle">This OTP will appear here once you're matched with a sender. Share it with the sender when they hand over the item to confirm the handover.</p>
                  <div className="otp-digits-row" aria-hidden>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="otp-digit">•</div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="otp-card" aria-live="polite">
                  <div className="otp-card-badge">Share with sender</div>
                  <div className="otp-card-icon-wrap" aria-hidden>{Icon.package}</div>
                  <h3 className="otp-card-title">Your Pickup OTP</h3>
                  <p className="otp-card-subtitle">
                    Show this OTP to your <strong>sender</strong> when they hand over the item.
                  </p>
                  <div className="otp-digits-row">
                    {(firstMileOTP ? String(firstMileOTP).split("") : ["•", "•", "•", "•", "•", "•"]).map((d, i) => (
                      <div key={i} className="otp-digit">{d}</div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="fd-btn fd-btn--primary fd-btn--block otp-card-cta"
                    onClick={handleCopy}
                  >
                    <span className="fd-btn-icon" aria-hidden>{copied ? Icon.check : Icon.copy}</span>
                    {copied ? "Copied!" : "Copy OTP"}
                  </button>
                  <p className="otp-card-warn">
                    <span className="otp-card-warn-icon" aria-hidden>{Icon.shield}</span>
                    Share only with your matched sender.
                  </p>
                </div>
              )
            )}

            {/* ---- FIRST MILE SUCCESS ---- */}
            {status === "IN_PROGRESS" && (
              <div className="success-card">
                <div className="success-card-icon" aria-hidden>{Icon.check}</div>
                <h3>You now have the parcel</h3>
                <p>The sender has arranged for the receiver to collect from you at the destination. You only need to carry it on your flight — pickup and drop are arranged by the sender and receiver.</p>
              </div>
            )}

            {/* ---- ITEM READY FOR PICKUP SLIDER (IN_PROGRESS only) ---- */}
            {status === "IN_PROGRESS" && !pickupConfirmed && !showPickupCard && (
              <div
                ref={readyTrackRef}
                className={`slider-track ${readySliderDone ? "slider-done" : ""}`}
                onMouseMove={(e) => onReadyDragMove(e.clientX)}
                onMouseUp={onReadyDragEnd}
                onMouseLeave={onReadyDragEnd}
                onTouchMove={(e) => onReadyDragMove(e.touches[0].clientX)}
                onTouchEnd={onReadyDragEnd}
              >
                <div className="runway-dashes" />
                <div className="slider-fill" style={{ width: readySliderX + THUMB }} />
                <span className="slider-label">
                  {readySliderDone ? "Item marked as ready" : <>Slide — item ready for pickup <span className="slider-label-icon" aria-hidden>{Icon.arrowRight}</span></>}
                </span>
                <div
                  className="slider-thumb"
                  style={{ transform: `translateX(${readySliderX}px)` }}
                  onMouseDown={(e) => { e.preventDefault(); onReadyDragStart(e.clientX); }}
                  onTouchStart={(e) => onReadyDragStart(e.touches[0].clientX)}
                  aria-hidden
                >
                  <span className="slider-thumb-icon" aria-hidden>{readySliderDone ? Icon.check : Icon.package}</span>
                </div>
              </div>
            )}

            {/* ---- PICKUP TIME CARD ---- */}
            {status === "IN_PROGRESS" && showPickupCard && (
              <div className="otp-card" style={{ textAlign: "center" }}>
                <div className="otp-card-icon-wrap" aria-hidden>{Icon.package}</div>
                <h3 className="otp-card-title">Item ready for pickup!</h3>
                <p className="otp-card-subtitle">When can the sender/receiver collect it? <em>(optional)</em></p>
                <input
                  type="time"
                  style={{ border: "1.5px solid #e5e5e5", borderRadius: 10, padding: "10px 14px", fontSize: "1rem", fontFamily: "inherit", width: "100%", maxWidth: 180, textAlign: "center", outline: "none", marginBottom: 12 }}
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                />
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  <button
                    className="fd-btn"
                    style={{ background: "transparent", color: "#888", border: "1.5px solid #ddd" }}
                    onClick={() => { setShowPickupCard(false); setReadySliderX(0); }}
                  >Cancel</button>
                  <button
                    className="fd-btn fd-btn--primary"
                    onClick={submitPickupReady}
                    disabled={pickupSaving}
                  >{pickupSaving ? "Saving…" : "Confirm"}</button>
                </div>
              </div>
            )}

            {/* ---- PICKUP CONFIRMED SUCCESS ---- */}
            {status === "IN_PROGRESS" && pickupConfirmed && (
              <div className="success-card">
                <div className="success-card-icon" aria-hidden>{Icon.check}</div>
                <h3>Item marked as ready</h3>
                {pickupTime && <p>Available from: <strong>{pickupTime}</strong></p>}
              </div>
            )}

            {/* ---- STEP 2 OTP ---- */}
            {!lmSuccess && status !== "COMPLETED" && (
              status !== "IN_PROGRESS" ? (
                <div className="otp-card is-locked">
                  <div className="otp-card-badge is-muted">Step 2 · Delivery OTP</div>
                  <div className="otp-card-icon-wrap" aria-hidden>{Icon.lock}</div>
                  <h3 className="otp-card-title">Delivery OTP</h3>
                  <p className="otp-card-subtitle">Unlocks once the sender confirms they've handed the item to you.</p>
                </div>
              ) : (
                <div className="otp-card">
                  <div className="otp-card-badge">Confirm delivery</div>
                  <div className="otp-card-icon-wrap" aria-hidden>{Icon.inbox}</div>
                  <h3 className="otp-card-title">Enter Delivery OTP</h3>
                  <p className="otp-card-subtitle">
                    When the receiver collects the item, ask them for the <strong>OTP shared by the sender</strong>
                    {" "}and enter it here to confirm handover.
                  </p>

                  <div className="otp-input-row" onPaste={handleLmPaste}>
                    {lmDigits.map((d, i) => (
                      <input
                        key={i}
                        ref={(el) => (lmRefs.current[i] = el)}
                        className="otp-box clarity-mask"
                        type="tel"
                        inputMode="numeric"
                        autoComplete={i === 0 ? "one-time-code" : "off"}
                        maxLength={1}
                        value={d}
                        aria-label={`Delivery OTP digit ${i + 1}`}
                        onChange={(e) => handleLmChange(e.target.value, i)}
                        onKeyDown={(e) => handleLmKeyDown(e, i)}
                      />
                    ))}
                  </div>
                  {lmError && <p className="otp-error" role="alert">{lmError}</p>}

                  <div
                    ref={sliderTrackRef}
                    className={`slider-track ${!otpComplete ? "slider-disabled" : ""} ${sliderDone ? "slider-done" : ""}`}
                    onMouseMove={(e) => onDragMove(e.clientX)}
                    onMouseUp={onDragEnd}
                    onMouseLeave={onDragEnd}
                    onTouchMove={(e) => onDragMove(e.touches[0].clientX)}
                    onTouchEnd={onDragEnd}
                    role="slider"
                    aria-label="Slide to confirm delivery"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={sliderPct}
                    aria-disabled={!otpComplete || sliderDone}
                    tabIndex={otpComplete && !sliderDone ? 0 : -1}
                    onKeyDown={onSliderKeyDown}
                  >
                    <div className="runway-dashes" />
                    <div className="slider-fill" style={{ width: sliderX + 60 }} />
                    <span className="slider-label">
                      {sliderDone
                        ? (lmLoading ? "Verifying…" : "Delivery confirmed")
                        : !otpComplete
                          ? "Enter OTP to enable"
                          : (
                            <>
                              Slide to confirm delivery
                              <span className="slider-label-icon" aria-hidden>{Icon.arrowRight}</span>
                            </>
                          )}
                    </span>
                    <div
                      className="slider-thumb"
                      style={{ transform: `translateX(${sliderX}px)` }}
                      onMouseDown={(e) => { e.preventDefault(); onDragStart(e.clientX); }}
                      onTouchStart={(e) => onDragStart(e.touches[0].clientX)}
                      aria-hidden
                    >
                      <span className="slider-thumb-icon" aria-hidden>{sliderDone ? Icon.check : Icon.planeFilled}</span>
                    </div>
                  </div>

                  <p className="slider-fallback">
                    Or{" "}
                    <button
                      type="button"
                      className="slider-fallback-btn"
                      disabled={!otpComplete || sliderDone}
                      onClick={() => setShowConfirmModal(true)}
                    >tap here to confirm</button>
                  </p>

                  <p className="otp-card-warn">
                    <span className="otp-card-warn-icon" aria-hidden>{Icon.alert}</span>
                    Only confirm after the receiver has physically collected the item.
                  </p>
                </div>
              )
            )}

            {(lmSuccess || status === "COMPLETED") && (
              <div className="success-card">
                <div className="success-card-icon" aria-hidden>{Icon.check}</div>
                <h3>Delivery complete</h3>
                <p>Thank you for delivering with TurantX.</p>
              </div>
            )}

            <RequestTimeline currentStep={timelineStepFor(status)} userType="TRAVELER" stamps={summary?.statusTimestamps} createdAt={summary?.createdAt} />

            <div className="trust-strip is-info">
              <span className="trust-strip-icon" aria-hidden>{Icon.chat}</span>
              <div className="trust-strip-text">
                <strong>Official contact only</strong>
                <p>We will only contact you by SMS from TRANTX or here on TurantChat. Please ignore messages from personal numbers.</p>
              </div>
            </div>

            <div className="trust-strip is-success">
              <span className="trust-strip-icon" aria-hidden>{Icon.shield}</span>
              <div className="trust-strip-text">
                <strong>Verified senders only</strong>
                <p>Every sender on TurantX is verified by our ops team before being matched with a traveller.</p>
              </div>
            </div>
          </div>
        )}

        {/* ---------- REQUEST TAB ---------- */}
        {activeTab === "REQUEST" && summary && (
          <div className="wl-panel" role="tabpanel">
            <div className="request-summary-card">
              <div className="summary-row">
                <span className="label">Request ID</span>
                <span className="value">TX-{summary.requestId}</span>
              </div>
              <div className="summary-row">
                <span className="label">Name</span>
                <span className="value">{summary.firstName} {summary.lastName}</span>
              </div>
              <div className="summary-row">
                <span className="label">Route</span>
                <span className="value">{summary.fromCity} → {summary.toCity}</span>
              </div>
              <div className="summary-row">
                <span className="label">Airline</span>
                <span className="value">{summary.airline || "—"}</span>
              </div>
              <div className="summary-row">
                <span className="label">Travel date</span>
                <span className="value">{summary.travelDate || "—"}</span>
              </div>
              <div className="summary-row">
                <span className="label">Leaving home by</span>
                <span className="value">{summary.departureTime || "—"}</span>
              </div>
              <div className="summary-row">
                <span className="label">Baggage space</span>
                <span className="value">{summary.baggageSpace ? `${summary.baggageSpace} kg` : "—"}</span>
              </div>
              <div className="summary-row">
                <span className="label">Space in</span>
                <span className="value">{summary.spaceAvailable || "—"}</span>
              </div>
              <div className="summary-row">
                <span className="label">Can carry</span>
                <span className="value">{summary.carryType || "—"}</span>
              </div>
            </div>
          </div>
        )}

        {/* ---------- SENDER TAB ---------- */}
        {activeTab === "SENDER" && (
          <div className="wl-panel" role="tabpanel">
            {!senderDetails ? (
              <div className="status-banner">
                <span className="status-banner-icon">{Icon.inbox}</span>
                <div className="status-banner-text">
                  <strong>Sender details loading…</strong>
                  <p>If this takes too long, try refreshing.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="request-summary-card">
                  <div className="otp-card-badge" style={{ marginBottom: 12 }}>Your matched sender</div>

                  <div className="summary-row">
                    <span className="label">Item</span>
                    <span className="value">{senderDetails.itemDetails?.itemName || "—"}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Weight</span>
                    <span className="value">{senderDetails.itemDetails?.totalWeight ? `${senderDetails.itemDetails.totalWeight} kg` : "—"}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Sender name</span>
                    <span className="value">{senderDetails.itemDetails?.senderName || "—"}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Route</span>
                    <span className="value">{senderDetails.from?.city || "—"} → {senderDetails.to?.city || "—"}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Phone</span>
                    <span className="value">
                      <a href={`tel:+91${senderDetails.phone}`} style={{ color: "#FF621A", fontWeight: 600 }}>
                        +91 {senderDetails.phone}
                      </a>
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Sending date</span>
                    <span className="value">{senderDetails.itemDetails?.sendingDate || "—"}</span>
                  </div>

                  {senderDetails.itemDetails?.instructions && (
                    <div className="summary-row">
                      <span className="label">Instructions</span>
                      <span className="value">{senderDetails.itemDetails.instructions}</span>
                    </div>
                  )}
                </div>

                {/* Report section */}
                <div className="otp-card" style={{ marginTop: 16, textAlign: "center" }}>
                  <div className="otp-card-icon-wrap" aria-hidden>{Icon.alert}</div>
                  <h3 className="otp-card-title">Item not received?</h3>
                  <p className="otp-card-subtitle">
                    If the sender did not hand over the item to you, report it here. Our team will contact you shortly.
                  </p>
                  {(reportSent || (itemReport && !itemReport.resolved)) ? (
                    // Pending report — survives refreshes (comes from the doc)
                    <div style={{ color: "#b45309", fontWeight: 600, fontSize: "0.88rem", marginTop: 8 }}>
                      ⏳ Report submitted{itemReport?.reportedAt ? ` on ${itemReport.reportedAt.slice(0, 10)}` : ""} — team will contact you shortly.
                    </div>
                  ) : (
                    <>
                      {itemReport?.resolved && (
                        // Ops resolved it — tell the traveller, then allow reporting again
                        <div style={{ color: "#0f9d58", fontWeight: 600, fontSize: "0.88rem", margin: "8px 0" }}>
                          ✅ Your report was resolved{itemReport.resolvedAt ? ` on ${itemReport.resolvedAt.slice(0, 10)}` : ""} by our team.
                        </div>
                      )}
                      <button
                        className="fd-btn fd-btn--block"
                        style={{ background: "#fff2f2", color: "#dc2626", border: "1.5px solid #fca5a5", marginTop: 8, fontWeight: 700 }}
                        onClick={reportItemNotReceived}
                        disabled={reportLoading}
                      >
                        {reportLoading ? "Submitting…" : itemReport?.resolved ? "⚠ Report again — Item not received" : "⚠ Report — Item not received"}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------- EARNING TAB ---------- */}
        {activeTab === "EARNING" && (
          <div className="wl-panel" role="tabpanel">

            {/* ── Route-campaign guarantee. Only this traveller sees it; the
                   general earning copy below stays unchanged for everyone else. ── */}
            {summary?.campaignEligible && summary?.campaignReward != null && (
              <div style={{
                background: "linear-gradient(135deg, #0f9d58 0%, #15803d 100%)",
                borderRadius: 16, padding: "16px 18px", color: "#fff", marginBottom: 16,
              }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, opacity: 0.85, letterSpacing: "0.08em", marginBottom: 6 }}>
                  GUARANTEED REWARD
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginBottom: 8 }}>
                  <span style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: 2 }}>₹</span>
                  <span style={{ fontSize: "1.9rem", fontWeight: 800, lineHeight: 1 }}>{summary.campaignReward}</span>
                </div>
                <p style={{ fontSize: "0.82rem", lineHeight: 1.5, margin: 0, opacity: 0.95 }}>
                  {/* Once ops marks it paid, the "we will call you" promise is stale and
                      would generate support calls — so state the outcome instead. */}
                  {earningStatus === "PAID" ? (
                    <>Paid. This was yours for your first trip on this route, <strong>whether or not
                    we found an item for you to carry</strong>.</>
                  ) : (
                    <>This is yours for your first trip on this route — <strong>even if we do not find
                    an item for you to carry</strong>. Paid within 7 days of your flight date, once we
                    confirm you took the flight. We will call you to collect your UPI ID.</>
                  )}
                </p>
              </div>
            )}

            {/* ── Current trip ── */}
            <p style={{ fontSize: "0.72rem", fontWeight: 700, color: "#999", letterSpacing: "0.06em", marginBottom: 10, textTransform: "uppercase" }}>Current Trip</p>

            {earningAmount != null ? (
              <>
                <div style={{
                  background: earningStatus === "PAID"
                    ? "linear-gradient(135deg, #0f9d58 0%, #1aa34a 100%)"
                    : earningStatus === "PROCESSING"
                    ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                    : "linear-gradient(135deg, #FF621A 0%, #e0521a 100%)",
                  borderRadius: 20,
                  padding: "24px 22px",
                  color: "#fff",
                  marginBottom: 12,
                  position: "relative",
                  overflow: "hidden",
                }}>
                  {/* decorative circles */}
                  <div style={{ position: "absolute", top: -28, right: -28, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
                  <div style={{ position: "absolute", bottom: -40, right: 30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />

                  {/* top row: label + emoji */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: "0.68rem", fontWeight: 700, opacity: 0.8, letterSpacing: "0.08em", marginBottom: 2 }}>
                        {earningStatus === "PAID" ? "YOU HAVE EARNED" : "YOU WILL EARN"}
                      </div>
                      {summary?.fromCity && summary?.toCity && (
                        <div style={{ fontSize: "0.76rem", opacity: 0.85, fontWeight: 500 }}>
                          ✈️ {summary.fromCity} → {summary.toCity}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: "2rem", opacity: 0.9 }}>💸</div>
                  </div>

                  {/* amount */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 2, marginBottom: 16 }}>
                    <span style={{ fontSize: "1.3rem", fontWeight: 800, marginTop: 8, opacity: 0.85 }}>₹</span>
                    <span style={{ fontSize: "3.6rem", fontWeight: 900, lineHeight: 1, letterSpacing: "-2px" }}>
                      {earningAmount.toLocaleString("en-IN")}
                    </span>
                  </div>

                  {/* status badge */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "rgba(255,255,255,0.2)", backdropFilter: "blur(4px)",
                      borderRadius: 999, padding: "5px 14px",
                      fontSize: "0.78rem", fontWeight: 700, border: "1px solid rgba(255,255,255,0.25)",
                    }}>
                      {earningStatus === "PAID" && "✅ Paid"}
                      {earningStatus === "PROCESSING" && "🔄 Processing"}
                      {earningStatus === "PENDING" && "⏳ Payment pending"}
                    </div>
                    {summary?.travelDate && (
                      <div style={{ fontSize: "0.72rem", opacity: 0.75 }}>{summary.travelDate}</div>
                    )}
                  </div>
                </div>

                {/* Payment timeline */}
                <div className="request-summary-card" style={{ marginBottom: 16 }}>
                  {[
                    { label: "Match confirmed", done: ["MATCHED","IN_PROGRESS","COMPLETED"].includes(status) },
                    { label: "Delivery completed", done: status === "COMPLETED" },
                    { label: "Payment processing", done: earningStatus === "PROCESSING" || earningStatus === "PAID" },
                    { label: "Payment sent to UPI", done: earningStatus === "PAID" },
                  ].map((step, i, arr) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < arr.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                        background: step.done ? "#0f9d58" : "#f0f0f0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "0.72rem", color: step.done ? "#fff" : "#aaa", fontWeight: 700,
                      }}>
                        {step.done ? "✓" : i + 1}
                      </div>
                      <span style={{ fontSize: "0.84rem", color: step.done ? "#1a1a2e" : "#999", fontWeight: step.done ? 600 : 400 }}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{
                background: "#f8f9fb", border: "1.5px dashed #d0d0d0",
                borderRadius: 16, padding: "28px 20px", textAlign: "center", marginBottom: 16,
              }}>
                <div style={{ fontSize: "2rem", marginBottom: 8 }}>💰</div>
                <div style={{ fontWeight: 700, color: "#1a1a2e", marginBottom: 4, fontSize: "0.92rem" }}>Earning not set yet</div>
                <div style={{ fontSize: "0.78rem", color: "#667781", lineHeight: 1.5 }}>
                  Our team will update your earning once the trip is finalised.
                </div>
              </div>
            )}

            {/* ── Overall earnings ── */}
            <p style={{ fontSize: "0.72rem", fontWeight: 700, color: "#999", letterSpacing: "0.06em", margin: "4px 0 10px", textTransform: "uppercase" }}>Overall</p>
            <div style={{
              background: "#f8f9fb", border: "1px solid #e5e5e5",
              borderRadius: 16, padding: "18px 20px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 16,
            }}>
              <div>
                <div style={{ fontSize: "0.76rem", color: "#667781", marginBottom: 4 }}>Total earned (all trips)</div>
                {overallEarning > 0 ? (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
                    <span style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f9d58", marginTop: 3 }}>₹</span>
                    <span style={{ fontSize: "2rem", fontWeight: 800, color: "#0f9d58", lineHeight: 1 }}>
                      {overallEarning.toLocaleString("en-IN")}
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: "0.84rem", color: "#aaa", fontWeight: 500, marginTop: 4 }}>
                    Complete your first trip to start earning 🚀
                  </div>
                )}
              </div>
              <div style={{ fontSize: "2.2rem" }}>{overallEarning > 0 ? "🏆" : "🎯"}</div>
            </div>

            <div className="trust-strip is-info">
              <span className="trust-strip-icon" aria-hidden>{Icon.shield}</span>
              <div className="trust-strip-text">
                <strong>Payment via UPI</strong>
                <p>
                  {summary?.campaignEligible
                    ? "Trip earnings are sent to your registered UPI ID after delivery confirmation. Your guaranteed reward above is paid separately, even without a delivery. Message us on TurantChat for any queries."
                    : "Sent to your registered UPI ID after delivery confirmation. Message us on TurantChat for any queries."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ---------- FAQ TAB ---------- */}
        {activeTab === "FAQ" && (
          <div className="wl-panel" role="tabpanel">
            <div className="faq-list">
              {[
                {
                  q: "How will I know when I'm matched?",
                  a: "We'll notify you through SMS or TurantChat with the request details. You can then choose to accept or decline.",
                },
                {
                  q: "What kind of items will I carry?",
                  a: "Only document envelopes, such as passports, contracts, medical reports, and other approved documents that comply with airline and customs regulations. Every request is reviewed by our team before it reaches you, and you inspect the document yourself before accepting it.",
                },
                {
                  q: "Can I decline a match?",
                  a: "Absolutely. Accepting a request is always your choice. If it doesn't suit your plans or you're not comfortable carrying it, simply decline. There are no penalties.",
                },
                {
                  q: "What if my flight changes or gets cancelled?",
                  a: "Inform us through TurantChat as soon as possible. We'll update the sender and arrange an alternative if needed. There are no penalties for schedule changes.",
                },
                {
                  q: "Do I earn anything for this?",
                  a: "Yes. Every approved delivery has a fixed earning shared with you before you accept the request. Your payment is transferred to your registered UPI after the delivery is successfully completed.",
                },
                {
                  q: "How do I confirm pickup and delivery?",
                  a: "At pickup, the sender checks your boarding pass and enters your 6-digit Handover OTP to confirm you've received the document. At the destination, the receiver shares their Delivery OTP, which you enter to complete the delivery.",
                },
                {
                  q: "Is it safe to carry items through TurantX?",
                  a: "Yes. You're always in control. Every request is reviewed by our team, only approved documents that comply with airline and customs regulations are accepted, and you inspect the document yourself before accepting it—so you always know exactly what you're carrying. If you're ever uncomfortable, you can simply decline the request with no penalty.",
                },
              ].map((item, i) => (
                <div key={i} className="faq-item">
                  <button
                    type="button"
                    className={`faq-question${faqOpen === i ? " is-open" : ""}`}
                    onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                    aria-expanded={faqOpen === i}
                  >
                    <span>{item.q}</span>
                    <svg
                      className="faq-chevron"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      {faqOpen === i
                        ? <polyline points="18 15 12 9 6 15" />
                        : <polyline points="6 9 12 15 18 9" />}
                    </svg>
                  </button>
                  {faqOpen === i && (
                    <p className="faq-answer">{item.a}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="faq-ask">
              <p className="faq-ask-label">Got a question?</p>
              <textarea
                className="faq-ask-input"
                placeholder="Type your question here…"
                rows={3}
                value={faqQuestion}
                onChange={(e) => setFaqQuestion(e.target.value)}
              />
              <button
                type="button"
                className={`fd-btn fd-btn--primary fd-btn--block faq-ask-btn${!faqQuestion.trim() ? " faq-ask-btn--disabled" : ""}`}
                onClick={() => {
                  if (!faqQuestion.trim()) return;
                  window.dispatchEvent(new CustomEvent("tx-chat-open", { detail: { prefill: faqQuestion.trim() } }));
                  setFaqQuestion("");
                }}
                aria-disabled={!faqQuestion.trim()}
              >
                💬 Send on TurantChat →
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfirmModal && (
        <ConfirmModal
          title="Confirm Delivery"
          message="Are you sure the receiver has physically collected the item? This action cannot be undone."
          confirmText="Yes, confirm delivery"
          cancelText="Go back"
          loading={lmLoading}
          variant="primary"
          onConfirm={confirmDelivery}
          onCancel={() => { setShowConfirmModal(false); setSliderX(0); }}
        />
      )}
    </div>
  );
}
